import {FC} from 'react';
import {Button, Stack} from '@mui/material';
import {AdminBatchJob, AdminBatchSubmission, ExecutionMode} from '../../../types/adminBatch.ts';
import {supportsJobAction} from '../batchJobTypeGroups.ts';

interface JobActionBarProps {
    job: AdminBatchJob;
    submissions: AdminBatchSubmission[];
    onResolveStale: (jobId: number, staleMinutes: number, action: 'CANCEL' | 'RETRY_NEXT_PROVIDER') => void;
    onRetryFailed: (jobId: number, executionMode: ExecutionMode) => void;
    onReprocessAllCached: (jobId: number) => void;
    isPending: boolean;
}

export const JobActionBar: FC<JobActionBarProps> = ({
    job, submissions, onResolveStale, onRetryFailed, onReprocessAllCached, isPending,
}) => {
    const isTerminal = ['SUCCEEDED', 'FAILED', 'CANCELED'].includes(job.status);
    const hasFailedSubmissions = submissions.some(s => s.status === 'FAILED');

    return (
        <Stack direction="row" spacing={1} sx={{mt: 1.5}} flexWrap="wrap" useFlexGap>
            {/* 오래된 요청 취소 */}
            {supportsJobAction(job.jobType, 'RESOLVE_STALE_CANCEL') && (
                <Button size="small" variant="outlined" color="error" disabled={isPending}
                    onClick={() => {
                        const input = window.prompt('몇 분 이상 지난 SUBMITTED를 취소할까요?', '60');
                        if (input === null) return;
                        const staleMinutes = Number(input);
                        if (!Number.isFinite(staleMinutes) || staleMinutes <= 0) return;
                        if (!confirm(`job #${job.id}의 ${staleMinutes}분 이상 SUBMITTED를 취소할까요?`)) return;
                        onResolveStale(job.id, staleMinutes, 'CANCEL');
                    }}>
                    오래된 요청 취소
                </Button>
            )}

            {/* 오래된 요청 다음 AI */}
            {supportsJobAction(job.jobType, 'RESOLVE_STALE_RETRY') && (
                <Button size="small" variant="outlined" color="warning" disabled={isPending}
                    onClick={() => {
                        const input = window.prompt('몇 분 이상 지난 SUBMITTED를 다음 AI로 넘길까요?', '60');
                        if (input === null) return;
                        const staleMinutes = Number(input);
                        if (!Number.isFinite(staleMinutes) || staleMinutes <= 0) return;
                        if (!confirm(`job #${job.id}의 ${staleMinutes}분 이상 SUBMITTED를 다음 AI로 넘길까요?`)) return;
                        onResolveStale(job.id, staleMinutes, 'RETRY_NEXT_PROVIDER');
                    }}>
                    오래된 요청 다음 AI
                </Button>
            )}

            {/* 실패 전체 재시도 */}
            {supportsJobAction(job.jobType, 'RETRY_FAILED') && isTerminal && hasFailedSubmissions && (
                <Button size="small" variant="contained" color="warning" disabled={isPending}
                    onClick={() => {
                        const mode = prompt('실행 모드를 선택하세요:\n1: BATCH_API (기존 배치)\n2: STANDARD (실시간)', '2');
                        if (!mode) return;
                        const executionMode: ExecutionMode = mode === '1' ? 'BATCH_API' : 'STANDARD';
                        if (!confirm(`실패한 submission을 ${executionMode} 모드로 재시도할까요?`)) return;
                        onRetryFailed(job.id, executionMode);
                    }}>
                    실패 전체 재시도
                </Button>
            )}

            {/* 전체 캐시 재처리 */}
            {supportsJobAction(job.jobType, 'REPROCESS_ALL_CACHED') && isTerminal && (
                <Button size="small" variant="outlined" color="info" disabled={isPending}
                    onClick={() => {
                        if (!confirm(`job #${job.id}의 모든 submission을 캐시된 AI 응답으로 재처리할까요?\n(AI API를 다시 호출하지 않고, 기존 생성 완료 단어는 skip됩니다)`)) return;
                        onReprocessAllCached(job.id);
                    }}>
                    전체 캐시 재처리
                </Button>
            )}
        </Stack>
    );
};
